<script setup>

import {computed, inject, reactive, ref} from "vue";
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import EditableBtn from "../portal-editable-fields/EditableBtn.vue";
import PayoutFields from "./PayoutFields.vue";

const props = defineProps({
    amountUnit: {
        type: String,
        required: true
    },
    amountWithUnitsFormatted: {
        type: String,
        required: true
    },
    availableAmount: {
        type: [Number, String],
        default: null
    },
    preferredSettings: {
        type: Object,
        default: () => ({})
    },
    availableMethods: {
        type: Array,
        default: () => []
    },
    payoutInvoiceRequirements: {
        type: String,
        default: () => ({})
    },
    billToDetails: {
        type: [Array, Object],
    }
})

    const portalContentAndDesignStore = usePortalContentAndDesignStore()

    const emitter = inject('emitter')

    const modalName = computed(() => `request_payout_${props.amountUnit}`)

    const loading = reactive({
                requestPayout: false,
                refreshBankRequirements: false
            })

    const requestErrors = ref(null)

    const payoutFieldsRef = ref(null)

    const requestPayout = () => {

            if (loading.requestPayout || loading.refreshBankRequirements || !payoutFieldsRef.value.validatePaymentInfo()) {
                return;
            }

            requestErrors.value = null;
            loading.requestPayout = true;

            axios.post(portalContentAndDesignStore.getPayoutRequestRoute, {
                amount_unit: payoutFieldsRef.value.getAmountUnit(),
                reward_ids: payoutFieldsRef.value.getRewardIds(),
                settings: {
                    requested_payment_settings: payoutFieldsRef.value.getPaymentSettings()
                },
                invoice_path: payoutFieldsRef.value.getInvoicePath()
            })
                .then(response => {
                    const status = parseInt(response.data.status ?? -1);
                    if (status === 1 && response.data.payout) {
                        portalContentAndDesignStore.payoutsPageSettings.payouts.data.push(response.data.payout);
                        const remaining = response.data.available_payout;
                        if (remaining) {
                            portalContentAndDesignStore.payoutsPageSettings.payout_options = portalContentAndDesignStore.payoutsPageSettings.payout_options.map(
                                option => option.reward_amount_units === props.amountUnit ? remaining : option
                            );
                        } else {
                            portalContentAndDesignStore.payoutsPageSettings.payout_options = portalContentAndDesignStore.payoutsPageSettings.payout_options.filter(
                                option => option.reward_amount_units !== props.amountUnit
                            );
                        }
                        emitter?.emit('modal-off', modalName.value);
                    } else if (status === 0 && response.data.bank_transfer_fields) {
                        payoutFieldsRef.value.setBankTransferFields(response.data.bank_transfer_fields)
                        requestErrors.value = {additional_fields: response.data.errors}
                    } else {
                        requestErrors.value = response.data.errors || 'Could not request payout.';
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data && error.response?.data?.errors) {
                        requestErrors.value = Array.isArray(error.response.data.errors)
                            ? {additional_error: error.response.data.errors}
                            : error.response.data.errors;
                    }
                })
                .finally(() => loading.requestPayout = false);
    }

    const handleRefreshBankRequirementsLoading = (loadingInProgress) => {
        if (loadingInProgress) {
            requestErrors.value = null;
        }
        loading.refreshBankRequirements = loadingInProgress
    }

</script>
<template>
    <div>
        <modal
            :name="modalName"
            :title="portalContentAndDesignStore.translationSettings.portal_translation_features_request_payout || 'Request a payout'"
            size="small"
            @modalClose="() => requestErrors = null"
            :loading="loading.requestPayout || loading.refreshBankRequirements"
        >
            <template #toggle>
                <editable-btn
                    :type="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                    :corners="portalContentAndDesignStore.getPortalLayoutCornersClass"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_features_request_payout"
                />
            </template>
            <template #body>
                <payout-fields
                    ref="payoutFieldsRef"
                    :amount-unit="amountUnit"
                    :amount-with-units-formatted="amountWithUnitsFormatted"
                    :available-amount="availableAmount"
                    :commission-selection-enabled="!!portalContentAndDesignStore.payoutsPageSettings.commission_selection_enabled"
                    :preferred-settings="preferredSettings"
                    :available-methods="availableMethods"
                    :request-errors="requestErrors"
                    :payout-invoice-requirements="payoutInvoiceRequirements"
                    :bill-to-details="billToDetails"
                    @loading-refresh-bank-requirements="handleRefreshBankRequirementsLoading"
                    @request-errors="requestErrors = $event"
                />
            </template>
            <template #footer>
                <btn
                    type="primary"
                    :loading="loading.requestPayout"
                    :type="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                    @click="requestPayout"
                >
                    {{ portalContentAndDesignStore.translationSettings.portal_translation_general_request || 'Request' }}
                </btn>
            </template>
        </modal>
    </div>
</template>
